export const ADMIN_PERMISSIONS = {
  testManagementView: 'test_management.view',
  testManagementRead: 'test_management.read',
  testManagementWrite: 'test_management.write',
  invitesView: 'invites.view',
  invitesRead: 'invites.read',
  invitesWrite: 'invites.write',
  all: '*',
  commerceCreditsView: 'commerce.credits.view',
  commerceCreditsRead: 'commerce.credits.read',
  commerceCreditsWrite: 'commerce.credits.write',
  commerceCostsView: 'commerce.costs.view',
  commerceCostsRead: 'commerce.costs.read',
  commerceOrdersRead: 'commerce.orders.read',
  commerceOrdersRefund: 'commerce.orders.refund',
  gatewayPrincipalsView: 'gateway_principals.view',
  gatewayPrincipalsRead: 'gateway_principals.read',
  gatewayPrincipalsWrite: 'gateway_principals.write',
  pricingView: 'pricing.view',
  pricingRead: 'pricing.read',
  pricingWrite: 'pricing.write',
  llmRoutesView: 'llm_routes.view',
  llmRoutesRead: 'llm_routes.read',
  llmRoutesWrite: 'llm_routes.write',
  llmAccessView: 'llm_access.view',
  llmAccessRead: 'llm_access.read',
  llmAccessWrite: 'llm_access.write',
  alertAttributionView: 'alert_attribution.view',
  alertAttributionRead: 'alert_attribution.read',
  alertAttributionSensitive: 'alert_attribution.sensitive',
  desktopUpdatesView: 'desktop_updates.view',
  desktopUpdatesRead: 'desktop_updates.read',
  desktopUpdatesWrite: 'desktop_updates.write',
  sessionLogsView: 'session_logs.view',
  sessionLogsUpload: 'session_logs.upload',
  sessionLogsRead: 'session_logs.read',
  sessionLogsWrite: 'session_logs.write',
  taskPlatformView: 'task_platform.view',
  taskPlatformRead: 'task_platform.read',
  roomInspectorView: 'room_inspector.view',
  roomInspectorRead: 'room_inspector.read',
  roomCapacityView: 'room_capacity.view',
  roomCapacityRead: 'room_capacity.read',
  roomCapacityWrite: 'room_capacity.write',
} as const;

export type AdminPermission =
  (typeof ADMIN_PERMISSIONS)[keyof typeof ADMIN_PERMISSIONS];

export interface AdminPermissionState {
  isAdmin: boolean;
  permissionsEnabled: boolean;
  permissions: readonly string[];
  adminAccessError?: string;
}

export function hasAdminPermission(
  state: AdminPermissionState,
  requiredPermission: AdminPermission,
): boolean {
  if (!state.isAdmin) {
    return false;
  }
  if (
    requiredPermission === ADMIN_PERMISSIONS.roomCapacityView ||
    requiredPermission === ADMIN_PERMISSIONS.roomCapacityRead ||
    requiredPermission === ADMIN_PERMISSIONS.roomCapacityWrite
  ) {
    if (state.adminAccessError) return false;
    return (
      state.permissions.includes(ADMIN_PERMISSIONS.all) ||
      state.permissions.includes(requiredPermission) ||
      (requiredPermission !== ADMIN_PERMISSIONS.roomCapacityWrite &&
        state.permissions.includes(ADMIN_PERMISSIONS.roomCapacityWrite)) ||
      (requiredPermission === ADMIN_PERMISSIONS.roomCapacityView &&
        state.permissions.includes(ADMIN_PERMISSIONS.roomCapacityRead))
    );
  }
  if (
    requiredPermission === ADMIN_PERMISSIONS.roomInspectorRead ||
    requiredPermission === ADMIN_PERMISSIONS.roomInspectorView
  ) {
    // A failed refresh cannot reuse an old grant for private room data.
    if (state.adminAccessError) return false;
    return (
      state.permissions.includes(ADMIN_PERMISSIONS.all) ||
      state.permissions.includes(requiredPermission) ||
      (requiredPermission === ADMIN_PERMISSIONS.roomInspectorView &&
        state.permissions.includes(ADMIN_PERMISSIONS.roomInspectorRead))
    );
  }
  if (
    requiredPermission === ADMIN_PERMISSIONS.sessionLogsView ||
    requiredPermission === ADMIN_PERMISSIONS.sessionLogsUpload
  ) {
    return true;
  }
  if (!state.permissionsEnabled) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.all) ||
    state.permissions.includes(requiredPermission)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.invitesWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.invitesRead ||
      requiredPermission === ADMIN_PERMISSIONS.invitesView)
  )
    return true;
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.invitesRead) &&
    requiredPermission === ADMIN_PERMISSIONS.invitesView
  )
    return true;
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.testManagementWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.testManagementRead ||
      requiredPermission === ADMIN_PERMISSIONS.testManagementView)
  )
    return true;
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.testManagementRead) &&
    requiredPermission === ADMIN_PERMISSIONS.testManagementView
  )
    return true;
  // Commerce credits: write implies read implies view.
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.commerceCreditsWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.commerceCreditsRead ||
      requiredPermission === ADMIN_PERMISSIONS.commerceCreditsView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.commerceCreditsRead) &&
    requiredPermission === ADMIN_PERMISSIONS.commerceCreditsView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.commerceCostsRead) &&
    requiredPermission === ADMIN_PERMISSIONS.commerceCostsView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.commerceOrdersRefund) &&
    requiredPermission === ADMIN_PERMISSIONS.commerceOrdersRead
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.gatewayPrincipalsWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.gatewayPrincipalsRead ||
      requiredPermission === ADMIN_PERMISSIONS.gatewayPrincipalsView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.gatewayPrincipalsRead) &&
    requiredPermission === ADMIN_PERMISSIONS.gatewayPrincipalsView
  ) {
    return true;
  }
  // Pricing: write implies read implies view (mirrors commerce).
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.pricingWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.pricingRead ||
      requiredPermission === ADMIN_PERMISSIONS.pricingView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.pricingRead) &&
    requiredPermission === ADMIN_PERMISSIONS.pricingView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.llmRoutesWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.llmRoutesRead ||
      requiredPermission === ADMIN_PERMISSIONS.llmRoutesView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.llmRoutesRead) &&
    requiredPermission === ADMIN_PERMISSIONS.llmRoutesView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.llmAccessWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.llmAccessRead ||
      requiredPermission === ADMIN_PERMISSIONS.llmAccessView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.llmAccessRead) &&
    requiredPermission === ADMIN_PERMISSIONS.llmAccessView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.alertAttributionSensitive) &&
    (requiredPermission === ADMIN_PERMISSIONS.alertAttributionRead ||
      requiredPermission === ADMIN_PERMISSIONS.alertAttributionView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.alertAttributionRead) &&
    requiredPermission === ADMIN_PERMISSIONS.alertAttributionView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.desktopUpdatesWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.desktopUpdatesRead ||
      requiredPermission === ADMIN_PERMISSIONS.desktopUpdatesView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.desktopUpdatesRead) &&
    requiredPermission === ADMIN_PERMISSIONS.desktopUpdatesView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.taskPlatformRead) &&
    requiredPermission === ADMIN_PERMISSIONS.taskPlatformView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.sessionLogsWrite) &&
    requiredPermission === ADMIN_PERMISSIONS.sessionLogsRead
  ) {
    return true;
  }
  return false;
}
